import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { RecentSearchItem, SearchResults } from '../../../results';
import { RecentSearchesService } from '../../../results/recent-searches.service';
import { SIDE_PANEL_SEARCH_PAGE_PATH } from 'src/app/bar/utils/constants';
import { SearchOptions, SearchResultContext, SearchService, SearchSession } from 'src/app/bar/services/search';
import { HomeSearchSettings } from '../../../home-search-popup/home-search-settings';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Filter, FilterChangeData, FilterViewDetails } from '@shared/components/filters/models';
import { Subscription } from 'rxjs';
import { windowSizeObserver } from '@shared/utils';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { RouterService } from '@shared/services/router.service';
import { cloneDeep } from 'lodash';
import { SourceSettings } from 'src/app/bar/services/search/client';
import { MemorySearchSettings } from 'src/app/bar/services/search/client/memory-search-client/memory-search-client';
import { Filters } from '@local/client-contracts';

@UntilDestroy()
@Component({
  selector: 'default-view-side-panel-home',
  templateUrl: './default-view-side-panel-home.component.html',
  styleUrls: ['./default-view-side-panel-home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultViewSidePanelHomeComponent implements OnInit, AfterViewInit {
  private readonly recentSearchSetting: MemorySearchSettings[] = [
    {
      type: 'recent-search',
      maxCount: 3,
      header: {
        title: 'Recent searches',
      },
    },
  ];
  private readonly DEFAULT_VIEW_SIDE_PANEL_ID = 'default-view-home-side-panel';
  private readonly FILTER_APP_WIDTH = 28;
  private readonly FILTER_APP_WIDTH_PADDING = 6;
  private readonly LIMIT_ITEMS_COUNT_FILTER: { [filterName: string]: FilterViewDetails } = {
    ['app']: { limitItemsCount: 9 },
  };
  private searchSession: SearchSession;
  private resultsFiltersSub: Subscription;
  private windowSize$ = windowSizeObserver();
  resultFilters = signal<Filter[]>([]);
  displayedContext: SearchResultContext;
  isLoading = true;

  @ViewChild('containerFilter') containerFilter: ElementRef;

  constructor(
    private recentSearches: RecentSearchesService,
    private filtersService: FiltersService,
    private resultsService: ResultsService,
    private routerService: RouterService,
    private searchService: SearchService
  ) {
    this.searchSession = this.searchService.getOrCreateSearchSession(this.DEFAULT_VIEW_SIDE_PANEL_ID);
  }

  ngOnInit(): void {
    this.setRecentSearchItems();
  }

  ngAfterViewInit(): void {
    this.windowSize$.pipe(untilDestroyed(this)).subscribe(() => {
      requestAnimationFrame(() => {
        if (this.containerFilter?.nativeElement) {
          this.updateLimitItemsCountFilter();
          this.setupFilters();
        }
      });
    });
  }

  private setupFilters() {
    this.resultsFiltersSub?.unsubscribe();
    this.resultsFiltersSub = null;
    const subject$ = this.filtersService.getFloatingResultsFilters({
      sessionName: this.DEFAULT_VIEW_SIDE_PANEL_ID,
      baseFilters: [{ name: 'app' }],
      viewDetailsPerFilter: this.LIMIT_ITEMS_COUNT_FILTER,
    });
    this.resultsFiltersSub = subject$.pipe(untilDestroyed(this)).subscribe((r) => {
      this.resultFilters.set(r);
    });
  }

  private setRecentSearchItems() {
    this.isLoading = true;
    const options: SearchOptions = {
      resetSession: true,
      query: '',
      sources: this.recentSearchSetting,
    };
    this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext) => {
        if (ctx.searchCompleted) {
          this.displayedContext = cloneDeep(ctx);
          this.displayedContext.items = ctx.items;
          this.isLoading = false;
        }
      });
  }

  private updateLimitItemsCountFilter() {
    const containerWidth = this.containerFilter.nativeElement.offsetWidth;
    const newItemCount = Math.floor(
      (containerWidth - this.FILTER_APP_WIDTH_PADDING) / (this.FILTER_APP_WIDTH + this.FILTER_APP_WIDTH_PADDING)
    );
    this.LIMIT_ITEMS_COUNT_FILTER['app'].limitItemsCount = newItemCount - 1;
  }

  recentSearchClicked(recentSearch: RecentSearchItem) {
    const position = this.displayedContext.items.filter((i) => i.type === 'recent-search').findIndex((elm) => elm === recentSearch);
    this.recentSearches.reSearch(recentSearch, position, null, SIDE_PANEL_SEARCH_PAGE_PATH);
  }

  async onFilterChange(data: FilterChangeData) {
    const url = this.buildUrlWithFilters(data);
    await this.routerService.navigateByUrl(url);
  }

  private buildUrlWithFilters(item: FilterChangeData): string {
    const filters: Filters.Values = { [item.applyType]: [item.changes.values?.[0]?.value] };
    const filtersUrl = this.filtersService.getFiltersAsUrlParams(filters);
    return `${SIDE_PANEL_SEARCH_PAGE_PATH}?${filtersUrl}`;
  }
}
